import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CreateStudentSuspensionData,
  MarkStudentSuspensionDayData,
  StudentSuspensionFilters,
  UpdateStudentSuspensionData,
  attendanceKeys,
  checkoutKeys,
  lunchDetentionsKeys,
  studentDirectoryKeys,
  studentSuspensionsApi,
} from '@/services';

export const studentSuspensionsKeys = {
  all: ['student-suspensions'] as const,
  list: (filters: StudentSuspensionFilters) => ['student-suspensions', 'list', filters] as const,
  mine: ['student-suspensions', 'mine'] as const,
  detail: (id: string) => ['student-suspensions', 'detail', id] as const,
  threads: (id: string) => ['student-suspensions', 'detail', id, 'threads'] as const,
  messages: (id: string, teacherId: string) => ['student-suspensions', 'detail', id, 'messages', teacherId] as const,
  unread: ['student-suspensions', 'unread'] as const,
};

export function useStudentSuspensionsQuery(filters: StudentSuspensionFilters = {}, enabled = true) {
  return useQuery({
    queryKey: studentSuspensionsKeys.list(filters),
    queryFn: () => studentSuspensionsApi.list(filters),
    enabled,
  });
}

export function useMyStudentSuspensionsQuery(enabled = true) {
  return useQuery({
    queryKey: studentSuspensionsKeys.mine,
    queryFn: () => studentSuspensionsApi.mine(),
    enabled,
  });
}

export function useStudentSuspensionQuery(id: string | undefined) {
  return useQuery({
    queryKey: studentSuspensionsKeys.detail(id ?? ''),
    queryFn: () => studentSuspensionsApi.get(id!),
    enabled: !!id,
  });
}

export function useStudentSuspensionUnreadCountQuery(enabled = true) {
  return useQuery({
    queryKey: studentSuspensionsKeys.unread,
    queryFn: () => studentSuspensionsApi.unreadCount(),
    enabled,
  });
}

export function useStudentSuspensionThreadsQuery(id: string | undefined) {
  return useQuery({
    queryKey: studentSuspensionsKeys.threads(id ?? ''),
    queryFn: () => studentSuspensionsApi.threads(id!),
    enabled: !!id,
    // Staff outside the student's audience get a 403 here; that is an answer, not a blip.
    retry: false,
  });
}

export function useStudentSuspensionMessagesQuery(id: string | undefined, teacherId: string | undefined) {
  return useQuery({
    queryKey: studentSuspensionsKeys.messages(id ?? '', teacherId ?? ''),
    queryFn: () => studentSuspensionsApi.messages(id!, teacherId!),
    enabled: !!id && !!teacherId,
  });
}

/**
 * A suspension rewrites the student's daily and lesson marks, their MSS run, their checkout
 * state and the staff-wide badge — every one of those screens must refetch, not only this list.
 */
function invalidateEverythingItTouches(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: studentSuspensionsKeys.all });
  void qc.invalidateQueries({ queryKey: studentDirectoryKeys.all });
  void qc.invalidateQueries({ queryKey: attendanceKeys.all });
  // The dashboard summary sits outside `attendanceKeys.all`'s `admin` prefix.
  void qc.invalidateQueries({ queryKey: ['attendance', 'summary'] });
  void qc.invalidateQueries({ queryKey: checkoutKeys.all });
  void qc.invalidateQueries({ queryKey: lunchDetentionsKeys.all });
}

export function useCreateStudentSuspensionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateStudentSuspensionData) => studentSuspensionsApi.create(data),
    onSuccess: () => invalidateEverythingItTouches(qc),
  });
}

export function useUpdateStudentSuspensionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStudentSuspensionData }) =>
      studentSuspensionsApi.update(id, data),
    onSuccess: () => invalidateEverythingItTouches(qc),
  });
}

export function useCancelStudentSuspensionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => studentSuspensionsApi.cancel(id),
    onSuccess: () => invalidateEverythingItTouches(qc),
  });
}

export function useMarkStudentSuspensionDayMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, date, data }: { id: string; date: string; data: MarkStudentSuspensionDayData }) =>
      studentSuspensionsApi.markDay(id, date, data),
    onSuccess: (_data, { id }) => void qc.invalidateQueries({ queryKey: studentSuspensionsKeys.detail(id) }),
  });
}

export function usePostStudentSuspensionNoteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => studentSuspensionsApi.postNote(id, body),
    onSuccess: (_data, { id }) => void qc.invalidateQueries({ queryKey: studentSuspensionsKeys.detail(id) }),
  });
}

export function usePostStudentSuspensionMessageMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, teacherId, body, files }: { id: string; teacherId: string; body: string; files: File[] }) =>
      studentSuspensionsApi.postMessage(id, teacherId, body, files),
    onSuccess: (_data, { id, teacherId }) => {
      void qc.invalidateQueries({ queryKey: studentSuspensionsKeys.messages(id, teacherId) });
      void qc.invalidateQueries({ queryKey: studentSuspensionsKeys.threads(id) });
    },
  });
}

export function useMarkStudentSuspensionThreadReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, teacherId }: { id: string; teacherId: string }) =>
      studentSuspensionsApi.markThreadRead(id, teacherId),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: studentSuspensionsKeys.threads(id) });
      void qc.invalidateQueries({ queryKey: studentSuspensionsKeys.unread });
    },
  });
}
